"use client";

import React from "react";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
function AuditFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-10 text-center space-y-4 animate-in fade-in zoom-in duration-300">
        <div className="rounded-full bg-primary/10 p-4">
          <CircleCheck className="h-12 w-12 text-primary" />
        </div>
        <h3 className="text-2xl font-semibold text-foreground">Richiesta Inviata</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4">
          Invia una nuova richiesta
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="full_name" rules={{
        required: "Il nome è obbligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Nome e Cognome</FormLabel>
              <FormControl>
                <Input placeholder="Mario Rossi" {...field} />
              </FormControl>
              <FormDescription className="sr-only">Inserisci il tuo nome completo.</FormDescription>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="company_name" rules={{
        required: "Il nome dell'azienda è obbligatorio"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Azienda</FormLabel>
              <FormControl>
                <Input placeholder="B-Tech S.r.l." {...field} />
              </FormControl>
              <FormDescription className="sr-only">Nome dell'azienda per cui richiedi l'audit.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {}
        <FormField control={control} name="email" rules={{
        required: "L'email è obbligatoria",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Inserisci un indirizzo email valido"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Indirizzo Email</FormLabel>
              <FormControl>
                <Input type="email" placeholder="mario.rossi@esempio.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {}
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel>Telefono <span className="text-muted-foreground text-xs font-normal">(Opzionale)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+39 333 1234567" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <FormField control={control} name="website_url" rules={{
      pattern: {
        value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
        message: "Inserisci un URL valido"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Sito Web <span className="text-muted-foreground text-xs font-normal">(Opzionale)</span></FormLabel>
            <FormControl>
              <Input type="url" placeholder="https://www.tuosito.it" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {}
      <FormField control={control} name="audit_scope" rules={{
      required: "Descrivi brevemente l'ambito dell'audit"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel>Ambito dell'Audit</FormLabel>
            <FormControl>
              <Textarea placeholder="Descrivi brevemente le aree che desideri analizzare (es. infrastruttura, cybersecurity, dispositivi, ecc.)" className="min-h-[120px] resize-y" {...field} />
            </FormControl>
            <FormDescription>
              Più dettagli fornisci, più precisa sarà la nostra valutazione preliminare.
            </FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md flex items-center gap-2 text-destructive text-sm">
          <CircleAlert className="h-4 w-4 shrink-0" />
          <p>{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto md:min-w-[200px] bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_-5px_var(--color-primary)]">
        {isSubmitting ? <>
            <Loader className="mr-2 h-4 w-4 animate-spin" />
            Invio in corso...
          </> : "Richiedi Audit Gratuito"}
      </Button>
    </Form>;
}
export default function RichiediUnAudit() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return;
}